import { AppButtonPopup, FlexColumn } from "components"
import { FileUploadPicker, ImageEnlargeModal, SinglePreview } from "components/FileUploadPicker"
import { useState, useEffect, useMemo } from "react"
import { Button, Form, Input, TextArea } from "semantic-ui-react"
import styled from "styled-components"
import {
    AcceptFileType,
    cacheMediaUploadFiles,
    getQuestionMediaList,
    linkUploadedMediaCache,
    mapInspectionQuestionMediaFiles,
} from "utility"
import {
    MediaUtility,
    buildRotatedMediaItem,
    mergeLocalMediaState,
    replaceMediaItemInList,
} from "utility/media-utility"
import { InspectionService } from "utility/services/inspection"

const MediaContainer = styled.div`
    .media-item {
        width: 100px !important;
        height: 100px !important;
        min-width: 100px !important;
        max-width: 100px !important;
        flex: 0 0 100px !important;
        overflow: hidden;

        img {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover;
            object-position: center;
            display: block;
            border-radius: 8px;
        }
    }
`
export const CustomePreview = ({
    item,
    saveComment,
    onRotate,
    className,
    remove,
    onRemove,
    allowMetaEdit = true,
}) => {
    const [data, setData] = useState({
        Caption: "",
        Comment: "",
    })
    const [popupOpen, setPopupOpen] = useState(false)
    const [enlargeOpen, setEnlargeOpen] = useState(false)
    const previewUrl = MediaUtility.resolvePreviewUrl(item) || item?.url || ''
    const isImage = MediaUtility.checkIsImage({ ...item, url: previewUrl })

    useEffect(() => {
        setData({
            Caption: item.Caption,
            Comment: item.Comment,
        })
    }, [item])

    const onChange = (e, text) => {
        setData({
            ...data,
            [text.name]: text.value,
        })
    }

    const onClose = () => {
        if (
            (data.Comment ?? "") !== (item.Comment ?? "") ||
            (data.Caption ?? "") !== (item.Caption ?? "")
        ) {
            if (typeof saveComment === "function") {
                saveComment(item, data)
            }
        }
        setPopupOpen(false)
    }

    const handleDeleteHover = () => {
        setPopupOpen(false)
    }

    const handleDeleteClick = (file) => {
        setPopupOpen(false)
        setEnlargeOpen(false)
        if (onRemove) {
            onRemove(file)
        }
    }

    const handleRotateClick = (file) => {
        setPopupOpen(false)
        setEnlargeOpen(false)
        if (typeof onRotate === 'function') {
            onRotate(file)
        }
    }

    const handleSave = async () => {
        if (
            (data.Comment ?? "") !== (item.Comment ?? "") ||
            (data.Caption ?? "") !== (item.Caption ?? "")
        ) {
            if (typeof saveComment === "function") {
                await saveComment(item, data)
            }
        }
        setPopupOpen(false)
    }

    const handleCancel = () => {
        setData({
            Caption: item?.Caption || "",
            Comment: item?.Comment || "",
        })
        setPopupOpen(false)
    }

    const openPreview = () => {
        setPopupOpen(false)
        if (isImage && previewUrl) {
            setEnlargeOpen(true)
            return
        }
        if (previewUrl) {
            window.open(previewUrl, '_blank')
        }
    }

    if (!allowMetaEdit) {
        return (
            <>
                <SinglePreview
                    item={item}
                    disableOpen
                    onPreviewClick={openPreview}
                    className={`media-item ${className || ''}`}
                    remove={false}
                    onRotate={onRotate ? handleRotateClick : undefined}
                />
                <ImageEnlargeModal
                    open={enlargeOpen}
                    onClose={() => setEnlargeOpen(false)}
                    src={previewUrl}
                    alt={item?.Caption || item?.FileKey || 'Preview'}
                    rotation={item?.viewRotation || 0}
                />
            </>
        )
    }

    return (
        <>
            <AppButtonPopup
                className="comment-text-area-popup"
                on="hover"
                position="bottom left"
                positionFixed
                flowing
                offset={[0, 8]}
                pinned={false}
                open={popupOpen && !enlargeOpen}
                onOpen={() => {
                    if (!enlargeOpen) {
                        setPopupOpen(true)
                    }
                }}
                onClose={() => {
                    onClose()
                    setPopupOpen(false)
                }}
                triggerItem={
                    <SinglePreview
                        item={item}
                        disableOpen
                        onPreviewClick={openPreview}
                        className={`media-item ${className}`}
                        remove={remove}
                        onRemove={handleDeleteClick}
                        onDeleteHover={handleDeleteHover}
                        onRotate={onRotate ? handleRotateClick : undefined}
                    />
                }
                hoverable
                mouseEnterDelay={100}
                mouseLeaveDelay={250}
            >
                <FlexColumn>
                    <Form>
                        <Form.Field>
                            <label>Caption</label>
                            <Input
                                onChange={onChange}
                                value={data.Caption}
                                name="Caption"
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>Comments</label>
                            <TextArea
                                onChange={onChange}
                                value={data.Comment}
                                name="Comment"
                            />
                        </Form.Field>
                        <div className="d-flex justify-content-end gap-2">
                            <Button type="button" size="small" onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button type="button" size="small" primary onClick={handleSave}>
                                Save
                            </Button>
                        </div>
                    </Form>
                </FlexColumn>
            </AppButtonPopup>
            <ImageEnlargeModal
                open={enlargeOpen}
                onClose={() => setEnlargeOpen(false)}
                src={previewUrl}
                alt={item?.Caption || item?.FileKey || 'Preview'}
                rotation={item?.viewRotation || 0}
            />
        </>
    )
}
export const MediaTypeFilesBox = ({ addMedia, removeMedia, item }) => {
    const [files, setFiles] = useState([])
    const [isMediaUploading, setIsMediaUploading] = useState(false)

    const mediaSyncKey = useMemo(() => {
        const list = getQuestionMediaList(item)
        return list
            .map(
                m =>
                    `${m.MediaId ?? ''}:${m.FileKey ?? ''}:${m.SignedURL ?? m.FileURL ?? m.url ?? ''}:${m.Caption ?? ''}:${m.Comment ?? ''}`,
            )
            .join('|')
    }, [item?.Files, item?.Answers?.MediaResponse, item?.MediaAnswers])

    const canUpload = typeof addMedia === 'function'

    const onFileUpload = async data => {
        if (!canUpload) return
        cacheMediaUploadFiles(data)
        setIsMediaUploading(true)
        try {
            const result = await addMedia(item.Id, data, item.SectionId)
            const uploaded = Array.isArray(result) ? result.filter(Boolean) : []
            if (uploaded.length) {
                linkUploadedMediaCache(data, uploaded)
                setFiles(prev => {
                    const next = [...prev]
                    uploaded.forEach(file => {
                        const mediaId = file?.MediaId ?? file?.id
                        const idx = next.findIndex(
                            f =>
                                (mediaId != null
                                    && String(f?.MediaId ?? f?.id ?? '')
                                        === String(mediaId))
                                || (file?.FileKey
                                    && f?.FileKey === file.FileKey),
                        )
                        const previewUrl =
                            file?.SignedURL
                            || file?.FileURL
                            || file?.ImagePathUrl
                            || file?.url
                            || ''
                        const merged = {
                            ...(idx >= 0 ? next[idx] : {}),
                            ...file,
                            local: false,
                            url: previewUrl || (idx >= 0 ? next[idx]?.url : ''),
                            id: mediaId || file?.FileKey || file?.id,
                            file:
                                (idx >= 0 ? next[idx]?.file : null) ||
                                (data || []).find(
                                    d =>
                                        String(d?.MediaId ?? d?.id ?? '')
                                            === String(mediaId ?? '')
                                        || (file?.FileKey
                                            && d?.FileKey === file.FileKey),
                                )?.file,
                        }
                        if (idx >= 0) {
                            next[idx] = merged
                        } else {
                            next.push(merged)
                        }
                    })
                    return mapInspectionQuestionMediaFiles({
                        ...item,
                        Files: next,
                    })
                })
            }
        } catch (error) {
            console.error('Error uploading media:', error)
        } finally {
            setIsMediaUploading(false)
        }
    }

    useEffect(() => {
        setFiles(prev =>
            mergeLocalMediaState(
                mapInspectionQuestionMediaFiles(item),
                prev,
            ),
        )
    }, [mediaSyncKey, item?.Id])

    const onRemoveFile = async file => {
        if (file?.url?.startsWith('blob:')) {
            try {
                URL.revokeObjectURL(file.url)
            } catch (e) {
                // ignore revoke errors
            }
        }
        if (typeof removeMedia === 'function') {
            const updated = await removeMedia(item.Id, file, item.SectionId)
            setFiles(mapInspectionQuestionMediaFiles(updated || item))
            return
        }
        setFiles(prev =>
            prev.filter(
                ele => ele.id !== file.id && ele.MediaId !== file.MediaId,
            ),
        )
        if (canUpload && (file.MediaId || file.id)) {
            try {
                const mediaId = file.MediaId || file.id
                await InspectionService.deleteMedia(mediaId)
            } catch (error) {
                console.error('Error deleting media:', error)
            }
        }
    }

    const saveComment = async (mediaItem, meta) => {
        const hasChanged =
            (meta?.Comment ?? '') !== (mediaItem?.Comment ?? '') ||
            (meta?.Caption ?? '') !== (mediaItem?.Caption ?? '')
        if (!hasChanged || !canUpload) {
            return null
        }

        setFiles(prev =>
            prev.map(f => {
                const match =
                    (mediaItem?.MediaId != null
                        && String(f?.MediaId ?? '') === String(mediaItem.MediaId))
                    || (mediaItem?.id != null
                        && String(f?.id ?? '') === String(mediaItem.id))
                return match ? { ...f, ...meta } : f
            }),
        )

        try {
            const result = await addMedia(
                item.Id,
                [{ ...mediaItem, ...meta, local: false }],
                item.SectionId,
            )
            const updated = result?.[0]
            if (updated) {
                setFiles(prev =>
                    prev.map(f => {
                        const match =
                            (updated?.MediaId != null
                                && String(f?.MediaId ?? '')
                                    === String(updated.MediaId))
                            || (mediaItem?.id != null
                                && String(f?.id ?? '') === String(mediaItem.id))
                        if (!match) {
                            return f
                        }
                        return {
                            ...f,
                            ...updated,
                            Caption: String(updated?.Caption ?? '').trim()
                                ? updated.Caption
                                : meta?.Caption ?? f.Caption ?? '',
                            Comment: String(updated?.Comment ?? '').trim()
                                ? updated.Comment
                                : meta?.Comment ?? f.Comment ?? '',
                            id: updated.MediaId || f.id,
                            url:
                                updated.FileURL
                                || updated.SignedURL
                                || f.url,
                            name: updated.FileKey || f.name,
                        }
                    }),
                )
            }
        } catch (error) {
            console.error('Error saving media caption/comment:', error)
        }
        return null
    }

    const onRotateImage = async mediaItem => {
        if (!canUpload) return
        try {
            const { updatedItem } = await buildRotatedMediaItem(mediaItem, 90)
            setFiles(prev =>
                replaceMediaItemInList(prev, mediaItem, updatedItem),
            )
        } catch (error) {
            console.error('Error rotating image:', error)
        }
    }

    return (
        <MediaContainer>
            <FlexColumn>
                {canUpload ? (
                    <FileUploadPicker
                        maxFiles={500}
                        maxFileSize={500}
                        accept={AcceptFileType.all}
                        addedNewFiles={file => onFileUpload(file)}
                        files={files}
                        isUploading={isMediaUploading}
                        previewItemClassName="media-item"
                        remove
                        customePreview
                        previewItem={
                            <CustomePreview
                                saveComment={saveComment}
                                onRotate={onRotateImage}
                            />
                        }
                        onRemove={onRemoveFile}
                        thumbnail
                    />
                ) : (
                    // Read-only: show existing media only, no buttons
                    <FileUploadPicker
                        maxFiles={500}
                        maxFileSize={500}
                        accept={AcceptFileType.all}
                        addedNewFiles={() => {}}
                        files={files}
                        previewItemClassName="media-item"
                        remove={false}
                        customePreview
                        previewItem={
                            <CustomePreview saveComment={() => {}} />
                        }
                        onRemove={undefined}
                        thumbnail
                        disabled
                    />
                )}
            </FlexColumn>
        </MediaContainer>
    )
}